/**
 * Builds the FloatingProgressBar warning text for a partial label-generation run
 * (some examples labeled, others skipped e.g. by the content filter or another
 * provider error).
 *
 * Two lines:
 *   "Generated labels for <success> of <total> samples.
 *    <failed> samples couldn't be labeled due to <error type>."
 *
 * - Single reason: "<error type>" is the reason name (e.g. ContentPolicyViolationError).
 * - Multiple reasons: "<error type>" lists each reason, most common first
 *   (e.g. "RateLimitError, ContentPolicyViolationError").
 */
export function buildLabelGenWarningMessage({
    failedPrompts,
    totalPrompts,
    failureBreakdown,
    failureReason,
} = {}) {
    const entries = failureBreakdown
        ? Object.entries(failureBreakdown)
              .filter(([, count]) => count > 0)
              .sort((a, b) => b[1] - a[1])
        : [];

    const failed =
        failedPrompts ||
        entries.reduce((sum, [, count]) => sum + count, 0) ||
        0;

    const total = totalPrompts || 0;
    const success = Math.max(0, total - failed);

    const reason =
        entries.length > 1
            ? entries.map(([r]) => r).join(', ')
            : entries.length === 1
              ? entries[0][0]
              : failureReason || 'ContentPolicyViolationError';

    const pluralize = (count) => (count === 1 ? 'sample' : 'samples');

    return (
        `Generated labels for ${success} of ${total} ${pluralize(total)}.\n` +
        `${failed} ${pluralize(failed)} couldn't be labeled due to ${reason}.`
    );
}

/**
 * "Show details" lines for the partial label-generation warning: one line per
 * failure reason carrying the first captured provider/exception message, e.g.
 *   "RateLimitError (12 samples): 429 Too Many Requests ..."
 * Reasons are ordered by their failure count (most common first). Returns []
 * when no detailed messages were captured (older events / other producers),
 * so callers can hide the toggle entirely.
 */
export function buildLabelGenFailureDetailLines({ failureBreakdown, failureDetails } = {}) {
    const details = failureDetails && typeof failureDetails === 'object' ? failureDetails : {};
    const counts = failureBreakdown && typeof failureBreakdown === 'object' ? failureBreakdown : {};
    const reasons = Object.keys(details).filter((reason) => details[reason]);
    if (reasons.length === 0) return [];

    reasons.sort((a, b) => (counts[b] || 0) - (counts[a] || 0));

    const pluralize = (count) => (count === 1 ? 'sample' : 'samples');
    return reasons.map((reason) => {
        const count = counts[reason];
        const prefix =
            typeof count === 'number' && count > 0
                ? `${reason} (${count} ${pluralize(count)})`
                : reason;
        return `${prefix}: ${details[reason]}`;
    });
}
